import React from 'react';
import { Autocomplete, Tooltip, FormControl, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';
import theme from '../../themes/index.en';
import { CustomSelectProps } from './customSelectProps';

const CustomLabeledSelect: React.FC<CustomSelectProps> = ({
  label,
  name,
  type,
  value,
  onChange,
  required,
  error,
  helperText,
  SelectProps,
  multiple,
  maxHeight,
  disabled,
  readOnly,
  warningFlag,
  isRTL = false,
}) => {
  const options = React.Children.toArray(SelectProps?.children).map((option: any) => ({
    label: option.props.children,
    value: option.props.value,
    isHub: option.props.style?.color === theme.palette.success.dark,
    isSuppliersSourceCountry: option.props.style?.color === theme.palette.warning.contrastText,
  }));

  const findOption = (
    value: string | number | number[]
  ):
    | { label: any; value: any; isHub: boolean; isSuppliersSourceCountry: boolean }
    | { label: any; value: any; isHub: boolean; isSuppliersSourceCountry: boolean }[]
    | null => {
    if (multiple && Array.isArray(value)) {
      return options.filter((option) => value.includes(option.value));
    } else {
      return options.find((option) => option.value === value) || null;
    }
  };

  const handleChange = (event: any, newValue: any) => {
    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: multiple ? newValue.map((item: any) => item.value) : newValue ? newValue.value : null,
      },
    });
  };

  return (
    <FormControl
      fullWidth 
      error={error}
      sx={{
        direction: isRTL ? 'rtl' : 'ltr',
        textAlign: isRTL ? 'right' : 'left',
        marginTop: '15px',
      }}
    >
      {label && (
        <Typography
          component="label"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 500,
            display: 'block',
            marginBottom: '8px',
            textAlign: isRTL ? 'right' : 'left',
          }}
        >
          {label}
          {required && (
            <span
              style={{
                marginLeft: '5px',
                color: theme.palette.error.main,
              }}
            >
              *
            </span>
          )}
        </Typography>
      )}
      <Autocomplete
        multiple={multiple}
        options={options}
        getOptionLabel={(option) => option.label}
        value={findOption(value)}
        onChange={handleChange}
        renderOption={(props, option) => (
          <li
            {...props}
            style={{
              color: option.isHub
                ? theme.palette.success.dark
                : option.isSuppliersSourceCountry
                ? theme.palette.warning.contrastText
                : '',
              textAlign: isRTL ? 'right' : 'left',
            }}
          >
            {option.label}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            margin="normal"
            required={false}
            fullWidth
            id={name}
            name={name}
            type={type}
            disabled={disabled}
            error={error || false}
            helperText={helperText || ''}
            InputLabelProps={{
              shrink: true,
            }}
            FormHelperTextProps={{
              style: { color: theme.palette.error.main, textAlign: isRTL ? 'right' : 'left' },
            }}
            InputProps={{
              ...params.InputProps,
              style: {
                backgroundColor: warningFlag
                  ? theme.palette.warning.light
                  : theme.palette.background.default,
                color: theme.palette.text.primary,
                direction: isRTL ? 'rtl' : 'ltr', 
              },
              readOnly: readOnly,
            }}
          />
        )}
        ListboxProps={{
          style: { maxHeight: maxHeight || 300, direction: isRTL ? 'rtl' : 'ltr' },
        }}
        noOptionsText={<span style={{ color: theme.palette.text.primary }}>No options</span>}
        disabled={!options.length || disabled || readOnly}
        sx={{ marginTop: '-15px'}}
      />
    </FormControl>
  );
};

export default CustomLabeledSelect;
